import React, {
  FC,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import StateManager from 'react-select';
import { ProjectId } from 'api/models';
import { UserComponents } from 'components/atoms/select';
import { AssigneePicker } from 'components/molecules/assignee-picker';
import { Option as OptionT } from 'components/molecules/squad-picker/types';
import {
  AssigneeField,
  AssigneeValue,
} from 'components/organisms/tasks/fields';
import { ClassNameOnlyProps } from 'components/ozone-component';
import {
  AssigneePickerFilter,
  useAssigneeOptions,
} from 'hooks/use-assignee-options';
import { byLabel } from 'utils/comparators/by-label';

type Props = ClassNameOnlyProps & {
  field: AssigneeField;
  title: ReactNode;
  onChange: (latest: AssigneeValue[]) => void;
  onDone?: () => void;
  onClose?: () => void;
  onSave?: (callback) => void;
  projectId: ProjectId;
  isPrivate: boolean;
  selectRef: RefObject<StateManager>;
  startDate?: string | null;
  dueDate?: string | null;
};

// @TODO replace the assignee data
export const ControlledAssigneeFieldEditorPopover: FC<Props> = ({
  field,
  projectId,
  isPrivate,
  onChange: onChangeActual,
  onSave,
  onClose,
  onDone,
  startDate,
  dueDate,
}) => {
  const [, _projectId] = projectId;
  const [isDisabled, setIsDisabled] = useState(false);

  const options = useAssigneeOptions({
    projectId,
    isPrivate,
    filter: AssigneePickerFilter.All,
  });

  const { value: assignees = [] } = field;

  const optionsWithAllUsers = useAssigneeOptions({
    projectId,
    isPrivate,
    filter: AssigneePickerFilter.All,
    includeDeactivatedUsers: true,
  });

  const flattened = optionsWithAllUsers.flatMap((x) => x.options);

  const value = assignees
    .map(AssigneeValue.toString)
    .map((x) => flattened.find((o) => o.value === x)!)
    .filter(Boolean)
    .sort(byLabel)
    .map((x) => x as OptionT);
  const onChange = useCallback(
    (latest) =>
      onChangeActual(
        (latest ?? [])
          .sort(byLabel)
          .map((x) => x.value)
          .map(AssigneeValue.fromString),
      ),
    [onChangeActual],
  );

  const _onSave = useCallback(
    (values, callback) => {
      onChange(values);
      onSave?.(callback);
    },
    [onChange, onSave],
  );

  const popoverRef = useRef(null);

  const { Option, ...rest } = UserComponents;

  // doing this because we are not handling loading states when fetching assignee options, so the intial value sent to the picker is [].
  // todo remove this temp fix once that is handled.

  useEffect(() => {
    setTimeout(() => setIsDisabled(true), 200);
  }, []);

  return (
    <>
      {isDisabled ? (
        <div ref={popoverRef}>
          <AssigneePicker
            hideTrigger
            projectId={projectId}
            containerRef={popoverRef}
            onDone={onDone}
            onSave={_onSave}
            onClose={onClose}
            value={value}
            selectProps={{
              components: {
                ...rest,
              },
              options,
              onChange,
            }}
            isPrivate={isPrivate}
            startDate={startDate}
            dueDate={dueDate}
          />
        </div>
      ) : null}
    </>
  );
};
